import type { FC, FocusEventHandler, HTMLProps } from 'react';
import React, { useState, useEffect, useRef, useCallback } from 'react';

import { NotesLoading, NotesOverflowShade, NotesTextarea } from './styled';

interface INotesProps
    extends Omit<
        HTMLProps<HTMLDivElement>,
        'ref' | 'as' | 'onChange' | 'onBlur'
    > {
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    onBlur?: FocusEventHandler<HTMLTextAreaElement>;
    isLoading?: boolean;
}

export const Notes: FC<INotesProps> = ({
    value,
    onChange,
    placeholder,
    onBlur = () => {},
    isLoading = false,
    ...componentProps
}) => {
    const [hasFocus, setHasFocus] = useState(false);
    const [isExceedingHeight, setIsExceedingHeight] = useState(false);
    const textAreaRef = useRef<HTMLTextAreaElement>(null);

    // Calculates height needed for textarea
    const recalculateHeight = useCallback(
        (element: HTMLTextAreaElement) => {
            element.style.height = 'auto';
            const height = element.scrollHeight;
            element.style.height = `${height}px`;
            if (height > element.clientHeight) {
                setIsExceedingHeight(true);
            } else {
                setIsExceedingHeight(false);
            }
        },
        [setIsExceedingHeight]
    );

    // Resize textarea on initial mount
    useEffect(() => {
        textAreaRef.current && recalculateHeight(textAreaRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [textAreaRef.current, recalculateHeight]);

    // Focus textarea after save if was previously focused
    useEffect(() => {
        if (!isLoading && hasFocus) {
            textAreaRef.current?.focus();
        }
    }, [isLoading, hasFocus]);

    const handleInput = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement>) => {
            recalculateHeight(event.currentTarget);
        },
        [recalculateHeight]
    );

    const componentOnFocus = useCallback(() => {
        setHasFocus(true);
    }, [setHasFocus]);

    const componentOnBlur = useCallback(
        (event: React.FocusEvent<HTMLTextAreaElement>) => {
            setHasFocus(false);
            onBlur(event);
        },
        [setHasFocus, onBlur]
    );

    return (
        <div>
            <NotesTextarea
                ref={textAreaRef}
                id="notes-textarea"
                value={value}
                onChange={(event) =>
                    onChange(event.currentTarget?.value.trimStart())
                }
                onInput={handleInput}
                onFocus={componentOnFocus}
                onBlur={componentOnBlur}
                disabled={isLoading}
                placeholder={placeholder ?? 'Add Notes'}
            />
            <NotesOverflowShade
                shown={!isLoading && !hasFocus && isExceedingHeight}
            />
            <NotesLoading shown={isLoading}>Saving…</NotesLoading>
        </div>
    );
};

Notes.defaultProps = {
    placeholder: undefined,
};
